import { AzureConfiguration, LoginMethod } from "@/api/configurations";
import { createContext, Fragment, PropsWithChildren, ReactNode, useState } from "react";
import { InteractionType } from "@azure/msal-browser";
import { Loading } from "@/components";
import { msal } from "@/lib/msal";
import { MsalAuthenticationTemplate, MsalProvider, useIsAuthenticated } from "@azure/msal-react";
import { ServiceClient } from "@/util";
import { Token } from "@/types";
import { useLoginConfiguration } from "@/queries/configuration.queries";

interface ILoginContext {
  logout: () => void;
  isLoggedIn: boolean;
  setToken: (token: Token, withBarcode?: boolean) => void;
}

type AzureLoginProviderProps = {
  config: AzureConfiguration;
  children: ReactNode;
};

export const LoginContext = createContext({} as ILoginContext);

export function LoginProvider(props: PropsWithChildren) {
  const config = useLoginConfiguration();

  if (config.isLoading) {
    return <Loading />;
  }

  if (config.data?.loginMethod === LoginMethod.Azure) {
    return <AzureLoginProvider config={config.data.azure}>{props.children}</AzureLoginProvider>;
  } else {
    return <BasicLoginProvider>{props.children}</BasicLoginProvider>;
  }
}

function AzureLoginProvider({ config, children }: AzureLoginProviderProps) {
  const instance = msal.createInstance(config);

  return (
    <MsalProvider instance={instance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        loadingComponent={() => <Loading />}
        authenticationRequest={{ scopes: config.scopes }}
      >
        <AzureProvider config={config}>{children}</AzureProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

function AzureProvider(props: AzureLoginProviderProps) {
  const isLoggedIn = useIsAuthenticated();

  function logout() {
    msal.logout();
  }

  function setToken(token: Token, withBarcode?: boolean) {
    // Not used for Azure login.
  }

  return (
    <LoginContext.Provider value={{ logout, setToken, isLoggedIn }}>
      <Fragment>{props.children}</Fragment>
    </LoginContext.Provider>
  );
}

function BasicLoginProvider(props: PropsWithChildren) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return Boolean(localStorage.getItem("r_token"));
  });

  function logout() {
    ServiceClient.logout();
    setIsLoggedIn(false);
  }

  function setToken(token: Token, withBarcode?: boolean) {
    ServiceClient.setToken(token, withBarcode);
    setIsLoggedIn(true);
  }

  return (
    <LoginContext.Provider value={{ logout, setToken, isLoggedIn }}>
      <Fragment>{props.children}</Fragment>
    </LoginContext.Provider>
  );
}
